<template>
  <div class="block">
    <h2 class="page-head"><span class="page-heading">Certificate Program</span></h2>

    <div class="block-header">
      <h2><span class="title">1. Program Overview</span></h2>
    </div>
    <p>The Safe Food Handler Certificate Program is a self-guided certificate program designed to ensure that food service employees understand and can apply essential food safety principles. The program supports regulatory compliance, protects public health, and reinforces individual responsibility for safe food handling.</p>
    <p>The program is delivered through structured instructional content, knowledge checks, and a summative assessment. A certificate is issued to learners who successfully complete all program requirements.</p>
    <p>This certificate program is accredited in accordance with ANSI/ASTM E2659-24.</p>

    <div class="block-header">
      <h2><span class="title">2. Program Purpose</span></h2>
    </div>
    <p>The purpose of the Safe Food Handler Certificate Program is to provide food handlers with foundational knowledge and practical understanding of food safety hazards, prevention practices, and personal responsibilities necessary to reduce the risk of foodborne illness.</p>

    <div class="block-header">
      <h2><span class="title">3. Target Audience</span></h2>
    </div>
    <p>This program is intended for food service employees who handle food or food-contact surfaces as part of their job duties, including entry-level and experienced food handlers working in retail food establishments.</p>

    <div class="block-header">
      <h2><span class="title">4. Program Scope</span></h2>
    </div>
    <p>The program addresses food safety topics relevant to routine food handling operations. It does not prepare learners for food protection manager certification and does not confer supervisory, managerial, or professional authority.</p>

    <div class="block-header">
      <h2><span class="title">5. Program Access Requirements</span></h2>
    </div>
    <p>Participation in the Safe Food Handler Certificate Program requires the following:</p>
    <ul>
      <li>Access to a modern web browser and a reliable internet connection</li>
      <li>Use of a device capable of displaying web-based instructional content</li>
      <li>Ability to independently navigate a self-guided online learning environment</li>
      <li>Program instruction is delivered in English</li>
      <li>The program is self-paced; expected seat time is approximately 1 to 2 hours, depending on the learner's experience and reading speed</li>
    </ul>
    <p>These access requirements describe conditions for participation and do not constitute requisites for earning the certificate.</p>

    <div class="block-header">
      <h2><span class="title">6. Certificate Requisites</span></h2>
    </div>
    <p>To earn the Safe Food Handler Certificate, learners must:</p>
    <ul>
      <li>Access the program using a valid, fully paid license</li>
      <li>Complete all required instructional content</li>
      <li>Complete required quizzes or knowledge checks</li>
      <li>Pass the summative assessment</li>
    </ul>
    <p>Testing out of the program or waiving participation in the majority of learning activities is not permitted.</p>

    <div class="block-header">
      <h2><span class="title">7. Certificate Term</span></h2>
    </div>
    <p>Each certificate has a defined term of validity beginning on the date of issuance. The certificate term and applicable jurisdictional requirements are communicated to learners prior to enrollment and certificate issuance.</p>

    <div class="block-header">
      <h2><span class="title">7. Intended Learning Outcomes</span></h2>
    </div>
    <p>Upon successful completion of the program, learners will be able to:</p>
    <ul>
      <li>Identify common foodborne hazards and routes of contamination</li>
      <li>Apply time and temperature controls for food safety</li>
      <li>Demonstrate proper personal hygiene and handwashing practices</li>
      <li>Prevent cross-contamination and allergen cross-contact</li>
      <li>Follow safe procedures for receiving, storing, preparing, and serving food</li>
      <li>Clean and sanitize food-contact surfaces and equipment correctly</li>
    </ul>

    <div class="block-header">
      <h2><span class="title">8. Instructional Design and Delivery</span></h2>
    </div>
    <p>The program is self-guided and delivered through structured learning modules. Learner actions initiate automated activities that include:</p>
    <ul>
      <li>Presentation of instructional content</li>
      <li>Learning progression and knowledge checks</li>
      <li>Quizzing and summative assessment</li>
      <li>Scoring and pass/fail determination</li>
      <li>Certificate issuance</li>
    </ul>
    <p>No artificial intelligence (AI) is used in the delivery, assessment, scoring, or issuance of the certificate.</p>

    <div class="block-header">
      <h2><span class="title">9. Assessment and Evaluation</span></h2>
    </div>
    <p>Learners must pass a summative assessment to demonstrate achievement of the intended learning outcomes. Assessment scoring is criterion-referenced and aligned with the program's learning outcomes. Assessment security and identity verification controls are applied in accordance with documented procedures.</p>

    <div class="block-header">
      <h2><span class="title">10. Certificate Issuance and Use</span></h2>
    </div>
    <p>A certificate is issued to learners who meet all program requirements.</p>
    <p>The certificate confirms successful completion of the Safe Food Handler Certificate Program within the defined certificate term.</p>
    <p><strong> No designation is issued or authorized for use in association with this certificate.</strong></p>
    <p>The certificate does not confer a professional title, designation, acronym, post-nominal, licensure, or certification status. Certificate holders may not represent themselves using any title or status beyond possession of the issued certificate.</p>

    <div class="block-header">
      <h2><span class="title">11. Invalidating a Certificate</span></h2>
    </div>
    <p>A certificate may be invalidated if it is determined that the certificate should not have been issued due to violations of program rules, misuse of the certificate, misrepresentation, or breach of the license agreement. Learners will be notified of invalidation and required to cease use of the certificate.</p>

    <div class="block-header">
      <h2><span class="title">12. Fees, Cancellations, and Refunds</span></h2>
    </div>
    <p>Access to the program and certificate issuance requires payment of applicable license fees.</p>
    <p>Cancellation or refund of license fees may be requested by submitting a support request within the certificate program application. All such requests are reviewed at the sole discretion of Cogneti support staff. Approval of a cancellation or refund is not guaranteed.</p>

    <div class="block-header">
      <h2><span class="title">13. Nondiscrimination and Accessibility</span></h2>
    </div>
    <p>The program is open and available to all learners who meet the stated requisites. The certificate issuer does not discriminate on the basis of race, color, religion, national origin, disability, age, sex, sexual orientation, or any other protected category.</p>
    <p>Reasonable accommodations will be provided to learners with disabilities, consistent with applicable laws and documented procedures.</p>

    <div class="block-header">
      <h2><span class="title">14. Complaints, Appeals, and Support</span></h2>
    </div>
    <p>Learners may submit complaints, appeals, feedback, or support requests through designated channels. All submissions are recorded, reviewed, and addressed in accordance with documented procedures.</p>

    <div class="block-header">
      <h2><span class="title">15. Learner Responsibilities</span></h2>
    </div>
    <p>Learners are responsible for:</p>
    <ul>
      <li>Completing the program independently</li>
      <li>Complying with program rules and license terms</li>
      <li>Using the certificate only as permitted</li>
      <li>Providing accurate information during participation</li>
    </ul>

    <div class="block-header">
      <h2><span class="title">16. Program Evaluation and Continuous Improvement</span></h2>
    </div>
    <p>Program performance, assessment results, and learner feedback are reviewed periodically as part of program evaluation. Evaluation outcomes are used to support continuous improvement and ongoing alignment with stakeholder needs and regulatory expectations.</p>

    <div class="block-header">
      <h2><span class="title">17. Documentation and Records</span></h2>
    </div>
    <p>Records related to learner participation, assessment results, certificate issuance, and program evaluation are maintained in accordance with document and records control procedures.</p>

    <div class="block-header">
      <h2><span class="title">18. Program Notifications</span></h2>
    </div>
    <p>Learners are informed of program requirements, certificate conditions, and applicable policies through public-facing documents presented during enrollment and participation.</p>
  </div>
</template>

<script>
export default {
  name: 'CertificateProgramContent'
}
</script>



// WEBPACK FOOTER //
// src/components/views/guest/CertificateProgram/Program68.vue